import { useEffect, useMemo, useState } from 'react';
import ChartCard from '../components/ChartCard';
import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import ExportButtons from '../components/ExportButtons';
import LoadingState from '../components/LoadingState';
import StatCard from '../components/StatCard';
import { useActiveUpload } from '../contexts/ActiveUploadContext';
import { useDateRange } from '../contexts/DateRangeContext';
import { getDashboardSummary } from '../services/reportService';
import { exportRowsToCsv, exportSheetsToXlsx, flattenDashboardSummary } from '../utils/exporters';
import { formatNumber, formatPercent } from '../utils/formatters';

const countColumn = { key: 'project_count', label: 'Project Count', align: 'right' };
const percentColumn = { key: 'percent_of_total', label: '% of Total', align: 'right', render: (value) => formatPercent(value) };
const tatColumns = [
  { key: 'service_type', label: 'Service Type' },
  { key: 'promised_tat_hours', label: 'Promised TAT', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
  { key: 'project_count', label: 'Count', align: 'right' },
  { key: 'on_time', label: 'On-Time', align: 'right' },
  { key: 'late', label: 'Late', align: 'right' },
  { key: 'on_time_percent', label: 'On-Time %', align: 'right', render: (value) => formatPercent(value) },
];

function getKpis(summary) {
  const wanted = ['initial_delivered', 'rework_delivered', 'initial_on_time_percent', 'rework_on_time_percent', 'avg_ready_for_design'];
  const kpis = summary?.kpis || [];
  const filtered = wanted.map((key) => kpis.find((item) => item.key === key)).filter(Boolean);
  return filtered.length ? filtered : kpis.slice(0, 5);
}

function statusRows(rows = []) {
  return rows.map((row) => ({ ...row, project_count: Number(row.project_count || 0) }));
}

function qualityStatusRows(rows = [], milestoneType) {
  const source = rows.find((row) => row.milestone_type === milestoneType) || {};
  const noError = Number(source.no_error_count || 0);
  const blank = Number(source.blank_count || 0);
  const errorOther = Number(source.error_count || 0);
  const total = noError + errorOther + blank;

  return [
    {
      status: 'No Error',
      project_count: noError,
      percent_of_total: total ? Math.round((noError * 1000) / total) / 10 : 0,
    },
    {
      status: 'Error / Other',
      project_count: errorOther,
      percent_of_total: total ? Math.round((errorOther * 1000) / total) / 10 : 0,
    },
    {
      status: 'Blank',
      project_count: blank,
      percent_of_total: total ? Math.round((blank * 1000) / total) / 10 : 0,
    },
  ].filter((row) => row.project_count > 0 || total === 0);
}

export default function DashboardPage() {
  const { activeUpload, loading: uploadLoading } = useActiveUpload();
  const { startDate, endDate } = useDateRange();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeUpload?.id) return;
      setLoading(true);
      setError('');
      try {
        const data = await getDashboardSummary(activeUpload.id, startDate, endDate);
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeUpload?.id, startDate, endDate]);

  const initialByService = summary?.initialByService || [];
  const reworkByService = summary?.reworkByService || [];
  const initialOnTimeStatus = statusRows(summary?.initialOnTimeStatus || summary?.onTimeStatus || []);
  const reworkOnTimeStatus = statusRows(summary?.reworkOnTimeStatus || []);
  const idleRows = summary?.idleTimeSummary || [];
  const inHouseRows = summary?.inHouseExternalSummary || [];
  const designQualityMilestoneRows = summary?.designQualityByMilestone || [];
  const initialDesignQualityStatus = qualityStatusRows(designQualityMilestoneRows, 'Initial Project Delivery');
  const reworkDesignQualityStatus = qualityStatusRows(designQualityMilestoneRows, 'Restamp Required');
  const exportRows = useMemo(() => flattenDashboardSummary(summary), [summary]);

  if (uploadLoading) return <LoadingState message="Loading active upload..." />;
  if (!activeUpload) return <EmptyState title="No active report" message="Ask an admin to upload and activate a Project Milestone Report." />;

  return (
    <div className="page-stack workbook-dashboard">
      <div className="page-title-row">
        <div>
          <h2>Operations KPI Dashboard</h2>
          <p>Formula-driven dashboard replacement using the active Project Milestone Report. Date range: {startDate} to {endDate}</p>
        </div>
        <ExportButtons
          onCsv={() => exportRowsToCsv(exportRows, `dashboard-summary-${startDate}-to-${endDate}.csv`)}
          onXlsx={() => exportSheetsToXlsx({ Dashboard: exportRows }, `dashboard-summary-${startDate}-to-${endDate}.xlsx`)}
        />
      </div>
      <ErrorMessage message={error} />
      {loading ? <LoadingState message="Calculating dashboard..." /> : (
        <>
          <div className="stat-grid kpi-strip">
            {getKpis(summary).map((item) => <StatCard key={item.key} item={item} />)}
          </div>

          <div className="grid-two dashboard-chart-row">
            <div className="dashboard-block">
              <div className="section-bar">Projects Delivered by Service</div>
              <ChartCard title="Projects Delivered by Service" data={initialByService} xKey="service_type" yKey="project_count" type="donut" nameKey="service_type" valueKey="project_count" height={540} showLegend minCalloutPercent={0.03} maxCallouts={3} />
              <div className="panel workbook-table-panel">
                <div className="panel-header"><h3>Initial Project Delivery by Service</h3></div>
                <DataTable columns={[{ key: 'service_type', label: 'Service Type' }, countColumn, percentColumn]} rows={initialByService} />
              </div>
            </div>
            <div className="dashboard-block">
              <div className="section-bar">Rework Delivered by Service</div>
              <ChartCard title="Rework Delivered by Service" data={reworkByService} xKey="service_type" yKey="project_count" type="donut" nameKey="service_type" valueKey="project_count" height={540} showLegend minCalloutPercent={0.03} maxCallouts={3} />
              <div className="panel workbook-table-panel">
                <div className="panel-header"><h3>Restamp Required by Service</h3></div>
                <DataTable columns={[{ key: 'service_type', label: 'Service Type' }, countColumn, percentColumn]} rows={reworkByService} />
              </div>
            </div>
          </div>

          <div className="dashboard-block">
            <div className="section-bar">Internal Design Quality — Milestone QA Result</div>
            <div className="grid-two dashboard-chart-row">
              <div className="panel workbook-table-panel">
                <ChartCard
                  title="Initial Project Delivery: Internal Design Quality"
                  data={initialDesignQualityStatus}
                  xKey="status"
                  yKey="project_count"
                  type="pie"
                  nameKey="status"
                  valueKey="project_count"
                  height={340}
                  showLegend={false}
                  minCalloutPercent={0.01}
                  maxCallouts={3}
                />
                <DataTable columns={[{ key: 'status', label: 'Status' }, countColumn, percentColumn]} rows={initialDesignQualityStatus} />
              </div>
              <div className="panel workbook-table-panel">
                <ChartCard
                  title="Restamp Required: Internal Design Quality"
                  data={reworkDesignQualityStatus}
                  xKey="status"
                  yKey="project_count"
                  type="pie"
                  nameKey="status"
                  valueKey="project_count"
                  height={340}
                  showLegend={false}
                  minCalloutPercent={0.01}
                  maxCallouts={3}
                />
                <DataTable columns={[{ key: 'status', label: 'Status' }, countColumn, percentColumn]} rows={reworkDesignQualityStatus} />
              </div>
            </div>
          </div>

          <div className="grid-two dashboard-chart-row">
            <div className="dashboard-block">
              <ChartCard title="Initial Project Delivery: On-Time vs Late" data={initialOnTimeStatus} xKey="status" yKey="project_count" type="pie" nameKey="status" valueKey="project_count" height={340} showLegend={false} />
              <div className="panel workbook-table-panel">
                <div className="panel-header"><h3>Project Delivered On-Time (Initial Project Delivery)</h3></div>
                <DataTable columns={[{ key: 'status', label: 'Status' }, countColumn, percentColumn]} rows={initialOnTimeStatus} />
              </div>
              <div className="panel workbook-table-panel">
                <DataTable columns={tatColumns} rows={summary?.initialOnTimeByService || []} />
              </div>
            </div>
            <div className="dashboard-block">
              <ChartCard title="Restamp Required: On-Time vs Late" data={reworkOnTimeStatus} xKey="status" yKey="project_count" type="pie" nameKey="status" valueKey="project_count" height={340} showLegend={false} />
              <div className="panel workbook-table-panel">
                <div className="panel-header"><h3>Rework Delivered On-Time (Restamp Required)</h3></div>
                <DataTable columns={[{ key: 'status', label: 'Status' }, countColumn, percentColumn]} rows={reworkOnTimeStatus} />
              </div>
              <div className="panel workbook-table-panel">
                <DataTable columns={tatColumns} rows={summary?.reworkOnTimeByService || []} />
              </div>
            </div>
          </div>

          <div className="grid-two dashboard-chart-row">
            <div className="panel workbook-table-panel">
              <div className="panel-header"><h3>Average Idle Time</h3></div>
              <DataTable
                columns={[
                  { key: 'milestone_type', label: 'Milestone Type' },
                  { key: 'avg_ready_for_design', label: 'Avg Ready for Design (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
                  { key: 'avg_ready_for_pe', label: 'Avg Ready for PE (hrs)', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
                  { key: 'request_count', label: 'Request Count', align: 'right', render: formatNumber },
                ]}
                rows={idleRows}
              />
              <ChartCard
                title="Average Idle Time"
                data={idleRows}
                xKey="milestone_type"
                type="groupedBar"
                series={[
                  { key: 'avg_ready_for_design', name: 'Avg Ready for Design (hrs)' },
                  { key: 'avg_ready_for_pe', name: 'Avg Ready for PE (hrs)' },
                ]}
                height={330}
              />
            </div>
            <div className="panel workbook-table-panel">
              <div className="panel-header"><h3>Design Initial Project Delivery vs Restamp Required by In House vs External</h3></div>
              <DataTable
                columns={[
                  { key: 'in_house_vs_external', label: 'In House vs External' },
                  { key: 'initial_project_delivery', label: 'Initial Project Delivery', align: 'right' },
                  { key: 'restamp_required', label: 'Restamp Required', align: 'right' },
                  { key: 'revision_rate', label: 'Revision Rate', align: 'right', render: (value) => formatPercent(value) },
                ]}
                rows={inHouseRows}
              />
              <ChartCard
                title="Design Initial Project Delivery vs Restamp Required by In House vs External"
                data={inHouseRows}
                xKey="in_house_vs_external"
                type="groupedBar"
                series={[
                  { key: 'initial_project_delivery', name: 'Initial Project Delivery' },
                  { key: 'restamp_required', name: 'Restamp Required' },
                ]}
                height={330}
              />
            </div>
          </div>

          <div className="grid-two dashboard-chart-row">
            <div className="panel workbook-table-panel">
              <div className="panel-header"><h3>Average On-Hold Time by Request Type</h3></div>
              <DataTable
                columns={[
                  { key: 'reason', label: 'On-Hold Reason' },
                  { key: 'avg_on_hold_time', label: 'Avg On-Hold Time', align: 'right', render: (value) => Number(value || 0).toFixed(2) },
                  { key: 'project_count', label: 'Project Count', align: 'right' },
                ]}
                rows={summary?.avgOnHoldByReason || []}
                maxRows={25}
              />
            </div>
            <ChartCard title="Average On-Hold Time by Request Type" data={summary?.avgOnHoldByReason || []} xKey="reason" yKey="avg_on_hold_time" type="horizontalBar" height={420} />
          </div>

          <div className="grid-two dashboard-chart-row">
            <div className="panel workbook-table-panel">
              <div className="panel-header"><h3>CRE Restamp Reason Summary</h3></div>
              <DataTable columns={[{ key: 'reason', label: 'CRE Restamp Reason' }, countColumn, percentColumn]} rows={summary?.creRestampReasonSummary || []} />
              <ChartCard title="CRE Restamp Reason Summary" data={summary?.creRestampReasonSummary || []} xKey="reason" yKey="project_count" type="horizontalBar" height={320} />
            </div>
            <div className="panel workbook-table-panel">
              <div className="panel-header"><h3>On-Hold Reason Summary</h3></div>
              <DataTable columns={[{ key: 'reason', label: 'Resolved On-Hold Reason' }, countColumn, percentColumn]} rows={summary?.onHoldReasonSummary || []} maxRows={25} />
              <ChartCard title="On-Hold Reason Summary" data={summary?.onHoldReasonSummary || []} xKey="reason" yKey="project_count" type="horizontalBar" height={360} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
